import React, { useCallback, useState } from 'react';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'react-router-dom';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import IndexPageHeader from '@buyside/components/presentation/IndexPageHeader/indexPageHeader';
import IndexPageListArea from '@buyside/components/presentation/IndexPageListArea/indexPageListArea';
import IndexPaginationNav from '@buyside/components/presentation/IndexPaginationNav/indexPaginationNav';
import JobPostingIndexFilters, { renderOwnPostingsCheckbox } from './jobPostingIndexFilters';
import JobPostingItem from '@buyside/components/presentation/JobVaultIndexPage/JobPostingItem/jobPostingItem';
import Link from '@buyside/components/presentation/Link/link';
import Modal from '@buyside/components/presentation/Modal/modal';
import TooltipWhite from '@buyside/components/presentation/Tooltips/TooltipWhite/tooltipWhite';

import { validatePageParam } from '@buyside/util/numbers';
import { useIsMobile, BREAKPOINTS } from '@buyside/util/hooks/useIsMobile';
import { userJobApplicationsUrl, jobVaultNewUrl, userUrl } from '@buyside/util/urlHelper';
const { indexTypes, jobPostingIndexPerPage } = require('buyside/util/constantsFromRails.json');

import { useJobPostingIndexListQuery } from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/JobVaultIndexPage/jobVaultIndexPageStyles.scss';

const JobVaultIndexPage: React.FC = () => {
  const [isJobInquiriesModalOpen, setIsJobInquiriesModalOpen] = useState(false);

  const isMobile = useIsMobile(BREAKPOINTS.LARGE);
  const isMobileSmall = useIsMobile(BREAKPOINTS.SMALL);

  const [searchParams] = useSearchParams();
  validatePageParam(searchParams);

  const { data, previousData, loading, error } = useJobPostingIndexListQuery({
    variables: { searchParams: searchParams.toString(), indexType: indexTypes.jobs }
  });

  const currentUser = (data || previousData)?.currentUser;

  const renderCheckboxes = useCallback(
    () => renderOwnPostingsCheckbox(currentUser?.hasJobPostings),
    [currentUser?.hasJobPostings]
  );

  const renderFilters = useCallback(() => <JobPostingIndexFilters />, []);

  const renderOpenJobInquiriesModalButton = useCallback(
    () => (
      <Button
        size={BUTTON_SIZES.SMALL}
        text="Job Inquiries"
        type={BUTTON_TYPES.PRIMARY}
        onClick={() => setIsJobInquiriesModalOpen(true)}
      />
    ),
    []
  );

  const renderSideButtons = useCallback(
    () => (
      <>
        {loading || currentUser?.canPostJobs ? (
          <Button
            disabled={loading}
            size={BUTTON_SIZES.SMALL}
            text="Submit a Job"
            type={BUTTON_TYPES.MAJOR}
            url={jobVaultNewUrl()}
          />
        ) : (
          <>
            {currentUser?.missingJobPostReqDescription ? (
              <>
                <Button
                  className={styles.cannotSubmitButton}
                  disabled
                  size={BUTTON_SIZES.SMALL}
                  text="Submit a Job"
                  type={BUTTON_TYPES.MAJOR}
                  url={jobVaultNewUrl()}
                />
                <TooltipWhite
                  overlay={
                    <>
                      <div>{currentUser.missingJobPostReqDescription}</div>
                      <Link to={userUrl(currentUser.id)}>Edit Work Experience</Link>
                    </>
                  }
                  placement="bottom"
                >
                  <FontAwesomeIcon icon={faQuestionCircle} className={styles.questionIcon} />
                </TooltipWhite>
              </>
            ) : null}
          </>
        )}
        {!isMobileSmall && currentUser?.hasJobApplications && (
          <Button
            size={BUTTON_SIZES.SMALL}
            text="My Job Applications"
            type={BUTTON_TYPES.PRIMARY}
            url={userJobApplicationsUrl()}
          />
        )}
        <Modal
          isOpen={isJobInquiriesModalOpen}
          onClose={() => setIsJobInquiriesModalOpen(false)}
          renderOpenModalButton={renderOpenJobInquiriesModalButton}
          title="Job Inquiries?"
        >
          <section>
            <p>
              Please email <a href="mailto:jobs@sumzero.com">jobs@sumzero.com</a> with any questions
              regarding the Job Vault.
            </p>
            <p>All inquiries are treated with complete confidentiality.</p>
          </section>
        </Modal>
      </>
    ),
    [
      currentUser,
      isJobInquiriesModalOpen,
      isMobileSmall,
      loading,
      renderOpenJobInquiriesModalButton
    ]
  );

  if (error) throw error;

  return (
    <main className={styles.main}>
      <div className={styles.indexPageContainer}>
        <div className={styles.mainIndexPageArea}>
          <IndexPageHeader
            header="Jobs"
            renderCheckboxes={renderCheckboxes}
            renderFilters={renderFilters}
            renderSideButtons={renderSideButtons}
            resultCount={(data || previousData)?.jobPostingIndexListResultCount || 0}
            showMoreLess={useIsMobile(BREAKPOINTS.LARGER)}
          />
          <IndexPageListArea
            data={(data || previousData)?.jobPostingIndexList}
            loading={loading}
            currentUser={currentUser}
            card={JobPostingItem}
            isMobile={isMobile}
          />
          <IndexPaginationNav
            loading={loading}
            resultCount={(data || previousData)?.jobPostingIndexListResultCount || 0}
            resultLength={data?.jobPostingIndexList?.edges?.length || 0}
            resultsPerPage={jobPostingIndexPerPage}
          />
        </div>
      </div>
    </main>
  );
};

export default JobVaultIndexPage;
